<template>
  <div class="container-fluid py-4 dashboard-boutique">

    <!-- ================= HEADER ================= -->
    <div class="header-boutique d-flex justify-content-between align-items-center flex-wrap mb-4">

      <div class="d-flex align-items-center gap-3">
        <i class="bi bi-speedometer2 fs-2"></i>

        <div>
          <h2 class="fw-bold mb-0">Dashboard Boutique</h2>

          <small>
            Suivi global des ventes, stock et performance
            <span class="badge bg-light text-dark ms-2">
              {{ libellePeriode }}
            </span>
          </small>
        </div>
      </div>

      <!-- ================= FILTRES ================= -->
      <div class="d-flex align-items-center gap-2 flex-wrap">

        <!-- principaux -->
        <button
          v-for="periode in periodesPrincipales"
          :key="periode.value"
          class="btn"
          :class="filtre === periode.value ? 'btn-active' : 'btn-outline-light'"
          @click="changerFiltre(periode.value)"
        >
          {{ periode.label }}
        </button>

        <!-- secondaires -->
        <div class="dropdown">
          <button
            class="btn btn-outline-light dropdown-toggle"
            type="button"
            data-bs-toggle="dropdown"
          >
            Plus
          </button>

          <ul class="dropdown-menu dropdown-menu-end shadow">
            <li v-for="periode in periodesSecondaires" :key="periode.value">
              <a
                class="dropdown-item"
                href="#"
                @click.prevent="changerFiltre(periode.value)"
              >
                {{ periode.label }}
              </a>
            </li>
          </ul>
        </div>

      </div>
    </div>

    <!-- ================= LOADER ================= -->
    <div v-if="isLoading" class="text-center py-5">
      <div class="spinner-border text-success"></div>
      <p>Chargement...</p>
    </div>

    <!-- ================= CONTENT ================= -->
    <div v-else>

      <!-- ================= STOCK ================= -->
      <h5 class="section-title">
        <i class="bi bi-boxes me-2"></i>Stock
      </h5>

      <div class="row g-3 mb-4">
        <div class="col-md-3" v-for="card in stockKpi" :key="card.title">
          <div class="card card-kpi">
            <div class="card-body d-flex justify-content-between">
              <div>
                <small>{{ card.title }}</small>
                <h4>{{ card.value }}</h4>
              </div>
              <i :class="card.icon"></i>
            </div>
          </div>
        </div>
      </div>

      <!-- ================= VENTES ================= -->
      <h5 class="section-title">
        <i class="bi bi-cart-check me-2"></i>Ventes
      </h5>

      <div class="row g-3 mb-4">
        <div class="col-md-3" v-for="card in ventesKpi" :key="card.title">
          <div class="card card-kpi">
            <div class="card-body d-flex justify-content-between">
              <div>
                <small>{{ card.title }}</small>
                <h4>{{ card.value }}</h4>
              </div>
              <i :class="card.icon"></i>
            </div>
          </div>
        </div>
      </div>

      <!-- ================= APPRO ================= -->
      <h5 class="section-title">
        <i class="bi bi-truck me-2"></i>Approvisionnement
      </h5>

      <div class="row g-3 mb-4">
        <div class="col-md-3" v-for="card in approKpi" :key="card.title">
          <div class="card card-kpi">
            <div class="card-body d-flex justify-content-between">
              <div>
                <small>{{ card.title }}</small>
                <h4>{{ card.value }}</h4>
              </div>
              <i :class="card.icon"></i>
            </div>
          </div>
        </div>
      </div>

      <h5 class="section-title" v-if="$store.getters.hasPermission('DASH_VIEW_RENTABILITE')">
  <i class="bi bi-graph-up-arrow me-2"></i>Rentabilité
</h5>

<div class="row g-3 mb-4" v-if="$store.getters.hasPermission('DASH_VIEW_RENTABILITE')">
  <div
    class="col-md-3"
    v-for="card in rentabiliteKpi"
    :key="card.title"
  >
    <div class="card card-kpi">
      <div class="card-body d-flex justify-content-between">
        <div>
          <small>{{ card.title }}</small>
          <h4>{{ card.value }}</h4>
        </div>

        <i :class="card.icon"></i>
      </div>
    </div>
  </div>
</div>

    </div>
  </div>
</template>

<script>
import api from "@/api";

export default {
  data() {
    return {
      isLoading: false,
      filtre: "jour",

      dashboard: {
        produits: {},
        stock: {},
        ventes: {},
        approvisionnement: {},
        rentabilite: {}
      },

      periodesPrincipales: [
        { value: 'jour', label: "Aujourd'hui" },
        { value: 'semaine', label: 'Cette semaine' },
        { value: 'mois', label: 'Ce mois-ci' },
        { value: 'annee', label: 'Cette année' }
      ],

      periodesSecondaires: [
        { value: 'hier', label: 'Hier' },
        { value: 'semaine_derniere', label: 'Semaine dernière' },
        { value: 'mois_dernier', label: 'Mois dernier' },
        { value: 'annee_derniere', label: 'Année dernière' }
      ]
    };
  },

  computed: {

    libellePeriode() {
      const all = [
        ...this.periodesPrincipales,
        ...this.periodesSecondaires
      ];

      return all.find(p => p.value === this.filtre)?.label || this.filtre;
    },

    stockKpi() {
      const s = this.dashboard.stock || {};
      return [
        { title: "Articles", value: s.articles ?? 0, icon: "bi bi-box bg-primary" },
        { title: "Lots", value: s.lots ?? 0, icon: "bi bi-box bg-primary" },
        
        { title: "Ruptures", value: s.ruptures ?? 0, icon: "bi bi-exclamation-triangle bg-danger" },
        { title: "Stock critique", value: s.stock_critique ?? 0, icon: "bi bi-exclamation-circle bg-warning" }
      ];
    },

    ventesKpi() {
      const v = this.dashboard.ventes || {};
      return [
        { title: "Total ventes", value: v.total ?? 0, icon: "bi bi-cart bg-primary" },
        { title: "CA", value: this.money(v.chiffre_affaires), icon: "bi bi-currency-exchange bg-success" },
        { title: "Annulées", value: v.annulees ?? 0, icon: "bi bi-x-circle bg-danger" },
        { title: "Quantité", value: v.quantite_vendue ?? 0, icon: "bi bi-boxes bg-info" }
      ];
    },

    approKpi() {
      const a = this.dashboard.approvisionnement || {};
      return [
        { title: "Entrées", value: a.total ?? 0, icon: "bi bi-arrow-down bg-primary" },
        { title: "Quantité", value: a.quantite_entree ?? 0, icon: "bi bi-stack bg-success" },
        { title: "Valeur", value: this.money(a.valeur), icon: "bi bi-wallet bg-warning" }
      ];
    },
    rentabiliteKpi() {
  const r = this.dashboard.rentabilite || {}
  const chiffreAffaires = Number(r.chiffre_affaires || 0)
  const coutAchat = Number(r.cout_achat || 0)
  const benefice = chiffreAffaires - coutAchat

  const marge = chiffreAffaires > 0
  ? ((benefice / chiffreAffaires) * 100).toFixed(2)
  : 0

  return [
    {
      title: "CA",
      value: this.money(r.chiffre_affaires),
      icon: "bi bi-currency-exchange bg-primary"
    },
    {
      title: "Coût Achat",
      value: this.money(r.cout_achat),
      icon: "bi bi-cart-dash bg-warning"
    },
    {
      title: "Bénéfice",
      value: this.money(benefice),
      icon: "bi bi-graph-up-arrow bg-success"
    },
      {
      title: "Marge",
      value: `${marge}%`,
      icon: "bi bi-percent bg-info"
      },{ title: "Valeur stock", value: this.money(r.valeur_stock), icon: "bi bi-cash bg-success" },
   
  ]
}
  },

  mounted() {
    this.load();
  },

  methods: {

    async load() {
      this.isLoading = true;

      try {
        const res = await api.getItems(
          `dashboard_data?periode=${this.filtre}`
        );

        this.dashboard = res?.data || res || {
          stock: {},
          ventes: {},
          approvisionnement: {}
        };

      } catch (e) {
        console.error(e);
      } finally {
        this.isLoading = false;
      }
    },

    changerFiltre(value) {
      this.filtre = value;
      this.load();
    },

    money(v) {
      return Number(v || 0).toLocaleString("fr-FR") + " FCFA";
    }
  }
};
</script>

<style scoped>
.dashboard-boutique {
  background: #f5f7fb;
  min-height: 100vh;
}

.header-boutique {
  background: linear-gradient(135deg, #0f5132, #198754);
  color: white;
  padding: 20px;
  border-radius: 18px;
}

.section-title {
  margin: 20px 0 10px;
  font-weight: bold;
  color: #333;
}

.card-kpi {
  border: none;
  border-radius: 14px;
  box-shadow: 0 5px 15px rgba(0,0,0,0.05);
  transition: 0.2s;
}

.card-kpi:hover {
  transform: translateY(-4px);
}

.card-kpi i {
  font-size: 28px;
  padding: 10px;
  border-radius: 12px;
  color: white;
}

.btn-active {
  background: white;
  color: #198754;
  font-weight: bold;
}
</style>